import { type CostCenter } from '~/data/cost-center';

import { filter, map, pipe, sortByKey } from '@vestigas/utils';

import { type DeliveryNoteAdmin } from '../types';

import { getBulkEntityDetails } from './getBulkEntityDetails';
import { getPermittedEntityIds } from './getPermittedEntityIds';

/**
 * Returns the full permitted cost centers data for a given delivery note.
 * Extracts the list of permitted cost center ids and matches it with the full details from the fetched cost centers bulk data.
 * @param sgw - The sgw values of the delivery note to get the permitted cost centers data for.
 * @param costCenters - The bulk cost centers data to get the permitted cost centers data from.
 * @returns The permitted cost centers data for the given delivery note.
 */
export const getPermittedCostCentersData = (
  sgw: DeliveryNoteAdmin['sgw'],
  costCenters: CostCenter[],
) => {
  if (!sgw?.length || !costCenters?.length) {
    return [];
  }

  const permittedCostCenterIds = getPermittedEntityIds(
    { sgw } as DeliveryNoteAdmin,
    'costCenter',
  );

  // Map IDs to full cost center data
  const permittedCostCentersData = pipe(
    permittedCostCenterIds,
    map((id) => getBulkEntityDetails(id, costCenters)),
    filter(Boolean),
    (costCenter) => sortByKey(costCenter, 'name'),
  );

  return permittedCostCentersData;
};
